import { Component, inject, signal, OnInit, computed, DestroyRef, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PromptBotService } from '../../../../core/services/prompt-bot.service';
import { Feature, FeatureOption, FeatureOptionCategory, PromptVersion, ProductFeatureMapping, Product, TableActionEvent } from '../../../../core/models/prompt-bot.model';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { DropdownComponent, DropdownOption } from '../../../../shared/components/ui/dropdown/dropdown.component';
import { TextareaComponent } from '../../../../shared/components/ui/textarea-input/textarea.component';
import { PaginatedTableComponent, TableHeader } from '../../../../shared/components/tables/paginated-table/paginated-table.component';
import { AlertComponent } from '../../../../shared/components/alert/alert.component';
import { IconName, IconComponent } from '../../../../shared/icons/icon.component';
import { PromptEditorModalComponent } from './modals/prompt-editor-modal/prompt-editor-modal.component';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { PromptDetailModalComponent, PromptDetailData } from '../../../../shared/components/prompt-detail-modal/prompt-detail-modal.component';
import { AuditService } from '../../../../core/services/audit.service';
import { AuditAction, AuditEntityType } from '../../../../core/models/audit-log.model';

@Component({
    selector: 'app-prompt-manager',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonComponent, DropdownComponent, PaginatedTableComponent, AlertComponent, MatDialogModule, IconComponent],
    templateUrl: './prompt-manager.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./prompt-manager.component.scss']
})
export class PromptManagerComponent implements OnInit {
    private botService = inject(PromptBotService);
    private destroyRef = inject(DestroyRef);
    private dialog = inject(MatDialog);
    private auditService = inject(AuditService);

    @Output() actionChange = new EventEmitter<void>();

    products = signal<Product[]>([]);
    options = signal<FeatureOption[]>([]);
    allFeatures = signal<Feature[]>([]);
    prompts = signal<PromptVersion[]>([]);

    productId = signal(0);
    featureId = signal(0);
    optionId = signal(0);
    categoryId = signal(0);
    isApplied = signal(true);

    // Track the context that was explicitly 'Applied' via the filter button
    private appliedContext = { pid: 0, fid: 0, oid: 0, cid: 0 };

    successMessage = signal('');
    private previousPromptState: PromptVersion | null = null;

    String = String;

    productOptions = computed<DropdownOption[]>(() =>
        this.products().map(p => ({ value: String(p.id), label: p.name }))
    );

    featureOptions = computed<DropdownOption[]>(() =>
        this.allFeatures().map(f => ({
            value: String(f.id!),
            label: f.name
        }))
    );

    optionOptions = computed<DropdownOption[]>(() =>
        this.options().map(o => ({ value: String(o.id!), label: o.name }))
    );

    // Categories belong to the currently selected feature option (if any)
    selectedOptionCategories = computed<FeatureOptionCategory[]>(() => {
        const opt = this.options().find(o => o.id === this.optionId());
        return opt?.categories?.filter(c => c.is_active !== false) ?? [];
    });

    hasCategories = computed(() => this.selectedOptionCategories().length > 0);

    categoryOptions = computed<DropdownOption[]>(() =>
        this.selectedOptionCategories().map(c => ({ value: String(c.id), label: c.name }))
    );

    canApply = computed(() => {
        const baseSelection =
            this.productId() > 0 ||
            this.featureId() > 0 ||
            this.optionId() > 0;
        // When the chosen feature option exposes categories, the user must
        // pick one before applying the filter.
        if (this.hasCategories() && this.categoryId() === 0) {
            return false;
        }
        return baseSelection;
    });

    hasActiveFilters = computed(() =>
        this.productId() > 0 ||
        this.featureId() > 0 ||
        this.optionId() > 0 ||
        this.categoryId() > 0 ||
        this.appliedContext.pid > 0 ||
        this.appliedContext.fid > 0 ||
        this.appliedContext.oid > 0 ||
        this.appliedContext.cid > 0
    );

    nextVersionNumber = computed(() => {
        const list = this.prompts();
        if (list.length === 0) return 1;
        return Math.max(...list.map(p => p.version)) + 1;
    });

    headers: TableHeader[] = [
        { label: 'Release Tag', key: 'versionLabel', align: 'text-center' },
        { label: 'Deployment State', key: 'status_label', align: 'text-center' },
        { label: 'Release Preview', key: 'prompt_preview', align: 'text-left' },
        { label: 'Tag', key: 'manifest_tags', align: 'text-left', isHtml: true }
    ];

    actionOptions = [
        { label: 'View Details', value: 'view', iconName: 'eye' as IconName, isShow: true },
        { label: 'Edit', value: 'edit', iconName: 'pencil' as IconName, isShow: true },
        // { label: 'Delete', value: 'delete', iconName: 'trash' as IconName, isShow: true },
    ];

    ngOnInit() {
        this.loadProducts();
        this.loadAllFeatures();
        this.loadPrompts(); // Initial load for full list
    }

    loadProducts() {
        this.botService.getProducts()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data: Product[]) => this.products.set(data)
            });
    }

    loadAllFeatures() {
        this.botService.getFeatures()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data: Feature[]) => this.allFeatures.set(data)
            });
    }

    onProductChange(id: string | string[]) {
        const pid = Number(Array.isArray(id) ? id[0] : id);
        this.productId.set(pid);
        // Do not reset feature/option to allow independent filtering
    }

    onFeatureChange(id: string | string[]) {
        const fid = Number(Array.isArray(id) ? id[0] : id);
        this.featureId.set(fid);
        this.optionId.set(0);
        this.categoryId.set(0);
        this.fetchOptionsForFeature(fid);
    }

    private fetchOptionsForFeature(fid: number) {
        if (fid) {
            this.botService.getFeatureOptions(fid)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (data: FeatureOption[]) => this.options.set(data)
                });
        } else {
            this.options.set([]);
        }
    }

    onOptionChange(id: string | string[]) {
        const oid = Number(Array.isArray(id) ? id[0] : id);
        this.optionId.set(oid);
        // Reset category when the option changes; user must re-pick if the
        // newly selected option has categories.
        this.categoryId.set(0);
    }

    onCategoryChange(id: string | string[]) {
        const cid = Number(Array.isArray(id) ? id[0] : id);
        this.categoryId.set(cid);
    }

    applyFilters() {
        if (!this.canApply()) return;
        this.isApplied.set(true);
        // Lock in current signal values as the 'Applied' context for the table
        this.appliedContext = {
            pid: this.productId(),
            fid: this.featureId(),
            oid: this.optionId(),
            cid: this.categoryId()
        };
        this.loadPrompts();
    }

    clearFilters() {
        this.productId.set(0);
        this.featureId.set(0);
        this.optionId.set(0);
        this.categoryId.set(0);
        this.options.set([]);
        this.appliedContext = { pid: 0, fid: 0, oid: 0, cid: 0 };
        this.isApplied.set(false);
        this.loadPrompts();
    }

    loadPrompts(silent: boolean = false) {
        this.botService.getPrompts(this.appliedContext.pid, this.appliedContext.fid, this.appliedContext.oid, this.appliedContext.cid, { showLoader: !silent })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data: PromptVersion[]) => {
                    const processed = data.map(p => ({
                        ...p,
                        versionLabel: `v${p.version}`,
                        status_label: p.is_active ? '🟢 Deployed' : 'Archived',
                        prompt_preview: p.prompt_text.length > 70 ? p.prompt_text.substring(0, 70) + '...' : p.prompt_text,
                        manifest_tags: `
                            <div class="flex flex-wrap gap-1 items-center">
                                <span class="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 rounded text-[9px] font-bold uppercase tracking-tighter border border-indigo-200/50 dark:border-indigo-700/50">${p.product?.name || 'N/A'}</span>
                                <span class="px-1.5 py-0.5 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 rounded text-[9px] font-medium border border-slate-200 dark:border-slate-700">${p.feature?.name || 'N/A'}</span>
                                <span class="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 rounded text-[9px] font-medium border border-emerald-200/50 dark:border-emerald-700/50">${p.feature_option?.name || 'N/A'}</span>
                            </div>
                        `
                    })).sort((a, b) => b.version - a.version);
                    this.prompts.set(processed);
                }
            });
    }

    isFullySelected() {
        return this.isApplied();
    }

    openAddModal() {
        // Pre-fill modal with current filters if any
        const initialPrompt: Partial<PromptVersion> = {
            product_id: this.productId(),
            feature_id: this.featureId(),
            feature_option_id: this.optionId(),
            category_id: this.categoryId() || null
        };

        const dialogRef = this.dialog.open(PromptEditorModalComponent, {
            data: { prompt: initialPrompt },
            maxWidth: 'none',
            panelClass: 'custom-modal-panel'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.deployNewPrompt(result);
            }
        });
    }

    private deployNewPrompt(data: any) {
        const payload: PromptVersion = {
            product_id: data.product_id,
            feature_id: data.feature_id,
            feature_option_id: data.feature_option_id,
            category_id: data.category_id ?? null,
            prompt_text: data.prompt_text,
            version: this.nextVersionNumber(),
            is_active: true
        };

        this.botService.createPrompt(payload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res: any) => {
                    const selectedProduct = this.products().find(p => p.id === payload.product_id);
                    const productName = selectedProduct?.name || payload.product_id;
                    const productKey = selectedProduct?.keyname;
                    const featureName = this.allFeatures().find(f => f.id === payload.feature_id)?.name || payload.feature_id;
                    const selectedOption = this.options().find(o => o.id === payload.feature_option_id);
                    const optionName = selectedOption?.name || payload.feature_option_id;
                    const categoryName = payload.category_id
                        ? selectedOption?.categories?.find(c => c.id === payload.category_id)?.name || payload.category_id
                        : null;

                    this.auditService.createAuditLog({
                        entityType: AuditEntityType.PROMPT,
                        action: AuditAction.CREATE,
                        actionTitle: `Prompt Release Deployed: v${payload.version}`,
                        oldValue: null,
                        newValue: {
                            ...payload,
                            id: res.id,
                            productName,
                            featureName,
                            optionName,
                            categoryName
                        },
                        productKey,
                        note: `Deployed new prompt version v${payload.version} for product '${productName}', feature '${featureName}', option '${optionName}'${categoryName ? `, category '${categoryName}'` : ''}`
                    }).subscribe({
                        next: () => {
                            this.loadPrompts(true);
                            this.actionChange.emit();
                        },
                        error: () => {
                            // Even if audit log fails, we should still refresh the list to show the new prompt
                            this.loadPrompts(true);
                            this.actionChange.emit();
                        }
                    });
                }
            });
    }

    handleAction(event: TableActionEvent<PromptVersion>) {
        if (event.action === 'view') {
            this.openViewModal(event.row);
        } else if (event.action === 'edit') {
            this.openEditModal(event.row);
        } else if (event.action === 'delete') {
            this.confirmDelete(event.row);
        }
    }

    openViewModal(prompt: PromptVersion) {
        const product = this.products().find(p => p.id === prompt.product_id)?.name || 'N/A';
        const feature = this.allFeatures().find(f => f.id === prompt.feature_id)?.name || 'N/A';

        // We might not have all options loaded globally, but we can try to find it in current options list
        // or just rely on IDs if we don't have a label map. 
        // For a more robust solution, we'd need a way to look up labels by ID.
        const modalData: PromptDetailData = {
            title: `Release Manifest Detail - v${prompt.version}`,
            fields: [
                { label: 'Product', value: product },
                { label: 'Feature', value: feature },
                { label: 'Deployment State', value: prompt.is_active ? 'Active' : 'Archived' },
                // { label: 'Created At', value: prompt.created_at ? new Date(prompt.created_at).toLocaleString() : 'N/A' }
            ],
            highlightLabel: 'Implementation Prompt',
            highlightValue: prompt.prompt_text
        };

        this.dialog.open(PromptDetailModalComponent, {
            data: modalData,
            width: '700px',
            panelClass: 'custom-modal-panel'
        });
    }

    openEditModal(prompt: PromptVersion) {
        this.previousPromptState = { ...prompt };
        const dialogRef = this.dialog.open(PromptEditorModalComponent, {
            data: { prompt },
            maxWidth: 'none',
            panelClass: 'custom-modal-panel'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.updatePrompt(result);
            }
        });
    }

    private updatePrompt(data: any) {
        const payload: PromptVersion = {
            product_id: data.product_id,
            feature_id: data.feature_id,
            feature_option_id: data.feature_option_id,
            category_id: data.category_id ?? null,
            prompt_text: data.prompt_text,
            version: data.version || 1, // Keep original version or default
            is_active: true
        };

        this.botService.createPrompt(payload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res: any) => {
                    const oldValue: any = { id: this.previousPromptState?.id };
                    const newValue: any = { id: res.id || this.previousPromptState?.id };
                    const fields: (keyof PromptVersion)[] = ['product_id', 'feature_id', 'feature_option_id', 'category_id', 'prompt_text', 'version', 'is_active'];

                    fields.forEach(field => {
                        if (this.previousPromptState?.[field] !== payload[field as keyof PromptVersion]) {
                            oldValue[field] = this.previousPromptState?.[field];
                            newValue[field] = payload[field as keyof PromptVersion];
                        }
                    });

                    const updatedProduct = this.products().find(p => p.id === payload.product_id);
                    const productName = updatedProduct?.name || payload.product_id;
                    const productKey = updatedProduct?.keyname;
                    const featureName = this.allFeatures().find(f => f.id === payload.feature_id)?.name || payload.feature_id;

                    this.auditService.createAuditLog({
                        entityType: AuditEntityType.PROMPT,
                        action: AuditAction.UPDATE,
                        actionTitle: `Prompt Release Updated: v${payload.version}`,
                        oldValue,
                        newValue,
                        productKey,
                        note: `Updated prompt release version v${payload.version} for '${productName}' - '${featureName}'`
                    }).subscribe({
                        next: () => {
                            this.previousPromptState = null;
                            this.loadPrompts(true);
                            this.actionChange.emit();
                        },
                        error: () => {
                            this.previousPromptState = null;
                            this.loadPrompts(true);
                            this.actionChange.emit();
                        }
                    });
                }
            });
    }

    confirmDelete(prompt: PromptVersion) {
        if (!prompt.id) return;
        const dialogRef = this.dialog.open(ConfirmationModalComponent, {
            data: {
                title: 'Sever Release Link?',
                description: `This will permanently archive version v${prompt.version}. This action cannot be undone.`
            }
        });

        dialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.botService.deletePrompt(prompt.id!)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: () => {
                            const deletedProduct = this.products().find(p => p.id === prompt.product_id);
                            const productName = deletedProduct?.name || prompt.product_id;
                            const productKey = deletedProduct?.keyname;
                            const featureName = this.allFeatures().find(f => f.id === prompt.feature_id)?.name || prompt.feature_id;

                            this.auditService.createAuditLog({
                                entityType: AuditEntityType.PROMPT,
                                action: AuditAction.DELETE,
                                actionTitle: `Prompt Release Archived: v${prompt.version}`,
                                oldValue: {
                                    ...prompt,
                                    productName,
                                    featureName
                                },
                                newValue: null,
                                productKey,
                                note: `Archived prompt version v${prompt.version} for '${productName}' - '${featureName}'`
                            }).subscribe({
                                next: () => {
                                    this.loadPrompts(true);
                                    this.actionChange.emit();
                                },
                                error: () => {
                                    this.loadPrompts(true);
                                    this.actionChange.emit();
                                }
                            });
                        }
                    });
            }
        });
    }
}
